import React from "react";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { intl } from "LocaleWrapper";

import { Card, Checkbox, FormGroup, Input, Select, Button } from "@cofano/cck";
import { Box, Grid2 as Grid, Stack, Tab, Tabs, Tooltip } from "@mui/material";
import useTabs from "components/hooks/useTabs";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { FormArrayOnlyRows } from "components/cck/FormArray";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import NumberInput from "components/cck/NumberInput";

import { Language } from "utils";

import ProductSelect from "view/products/components/select/ProductSelect";
import { LocationQuayTabs } from "view/locations/utils/LocationUtils";
import { LocationSchemaInput, LocationSchemaOutput } from "view/locations/utils/location.schema";

import { CraneType, MooringSide } from "__generated__/enums";

const LocationQuaysForm = () => {

	const { control } = useFormContext<LocationSchemaInput, unknown, LocationSchemaOutput>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: "quays",
	});

	const [ quays ] = useWatch({ control, name: [ "quays" ] });

	const defaultQuay = {
		id: 0,
		name1: "",
		length: null,
		depth: null,
		latitude: null,
		longitude: null,
		loadingSpeedTon: null,
		loadingSpeedTeu: null,
		loadingSpeedM3: null,
		loadingSpeedMove: null,
		dischargingSpeedTon: null,
		dischargingSpeedTeu: null,
		dischargingSpeedM3: null,
		dischargingSpeedMove: null,
		bolders: [],
		cranes: [],
	};

	return (
		<Stack spacing={3}>
			{fields.map((item, index) => {
				return (
					<Grid spacing={0} container key={item.id}>
						<Grid size={{ xs: 12 }}>
							<Grid spacing={3} container>
								<Grid size={{ xs: 12, lg: 4, md: 4 }}>
									<Card
										title={Language("QUAY")}
										subtitle={quays?.[index]?.name1}
										actions={[ {
											onClick: () => remove(index),
											icon: <DeleteIcon />,
										} ]}
										collapsable>
										<React.Fragment>
											<FormGroup
												label={Language("QUAY_NAME")}
												labelPlacement="left-center"
												overflow="hidden"
												required>
												<Controller
													name={`quays.${index}.name1`}
													control={control}
													render={({ field, formState }) => (
														<React.Fragment>
															<Input {...field} />
															<FormErrorMessage name={field.name} errors={formState.errors} />
														</React.Fragment>
													)} />
											</FormGroup>

											<FormGroup label={Language("QUAY_LENGTH")} labelPlacement="left-center" overflow="hidden">
												<Controller
													name={`quays.${index}.length`}
													control={control}
													render={({ field, formState }) => (
														<React.Fragment>
															<NumberInput {...field} addonAfter="m" />
															<FormErrorMessage name={field.name} errors={formState.errors} />
														</React.Fragment>
													)} />
											</FormGroup>

											<FormGroup label={Language("QUAY_DEPTH")} labelPlacement="left-center" overflow="hidden">
												<Controller
													name={`quays.${index}.depth`}
													control={control}
													render={({ field, formState }) => (
														<React.Fragment>
															<NumberInput {...field} addonAfter="m" />
															<FormErrorMessage name={field.name} errors={formState.errors} />
														</React.Fragment>
													)} />
											</FormGroup>

											<FormGroup label={Language("MAPS_POSITIONS")} labelPlacement="left-center" overflow="hidden">
												<Grid spacing={3} container>
													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.latitude`}
															control={control}
															render={({ field }) => <NumberInput {...field} addonAfter="lat" />} />
													</Grid>

													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.longitude`}
															control={control}
															render={({ field }) => <NumberInput {...field} addonAfter="lng" />} />
													</Grid>
												</Grid>
											</FormGroup>

											<FormGroup label={Language("QUAY_LOADING_SPEED_TON")} labelPlacement="left-center" overflow="hidden">
												<Grid spacing={3} container>
													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.loadingSpeedTon`}
															control={control}
															render={({ field, formState }) => (
																<React.Fragment>
																	<NumberInput {...field} addonAfter={`${intl.formatMessage({ id: "TON" })}/h`} />
																	<FormErrorMessage name={field.name} errors={formState.errors} />
																</React.Fragment>
															)} />
													</Grid>

													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.loadingSpeedTeu`}
															control={control}
															render={({ field, formState }) => (
																<React.Fragment>
																	<NumberInput {...field} addonAfter="TEU/h" />
																	<FormErrorMessage name={field.name} errors={formState.errors} />
																</React.Fragment>
															)} />
													</Grid>
												</Grid>
											</FormGroup>

											<FormGroup label="" labelPlacement="left-center" overflow="hidden">
												<Grid spacing={3} container>
													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.loadingSpeedM3`}
															control={control}
															render={({ field, formState }) => (
																<React.Fragment>
																	<NumberInput {...field} addonAfter="m3/h" />
																	<FormErrorMessage name={field.name} errors={formState.errors} />
																</React.Fragment>
															)} />
													</Grid>

													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.loadingSpeedMove`}
															control={control}
															render={({ field, formState }) => (
																<React.Fragment>
																	<NumberInput {...field} addonAfter="move/h" />
																	<FormErrorMessage name={field.name} errors={formState.errors} />
																</React.Fragment>
															)} />
													</Grid>
												</Grid>
											</FormGroup>

											<FormGroup label={Language("QUAY_DISCHARGING_SPEED_TON")} labelPlacement="left-center" overflow="hidden">
												<Grid spacing={3} container>
													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.dischargingSpeedTon`}
															control={control}
															render={({ field, formState }) => (
																<React.Fragment>
																	<NumberInput {...field} addonAfter={`${intl.formatMessage({ id: "TON" })}/h`} />
																	<FormErrorMessage name={field.name} errors={formState.errors} />
																</React.Fragment>
															)} />
													</Grid>

													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.dischargingSpeedTeu`}
															control={control}
															render={({ field, formState }) => (
																<React.Fragment>
																	<NumberInput {...field} addonAfter="TEU/h" />
																	<FormErrorMessage name={field.name} errors={formState.errors} />
																</React.Fragment>
															)} />
													</Grid>
												</Grid>
											</FormGroup>

											<FormGroup label="" labelPlacement="left-center" overflow="hidden">
												<Grid spacing={3} container>
													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.dischargingSpeedM3`}
															control={control}
															render={({ field, formState }) => (
																<React.Fragment>
																	<NumberInput {...field} addonAfter="m3/h" />
																	<FormErrorMessage name={field.name} errors={formState.errors} />
																</React.Fragment>
															)} />
													</Grid>

													<Grid size={{ lg: 6, md: 6, xs: 12 }}>
														<Controller
															name={`quays.${index}.dischargingSpeedMove`}
															control={control}
															render={({ field, formState }) => (
																<React.Fragment>
																	<NumberInput {...field} addonAfter="move/h" />
																	<FormErrorMessage name={field.name} errors={formState.errors} />
																</React.Fragment>
															)} />
													</Grid>
												</Grid>
											</FormGroup>
										</React.Fragment>
									</Card>
								</Grid>

								<Grid size={{ xs: 12, lg: 8, md: 8 }}>
									<Quay quayIndex={index} />
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			})}

			<Box>
				<Button
					icon={<AddIcon />}
					onClick={() => append(defaultQuay)}
					size="small"
					variant="outlined" />
			</Box>
		</Stack>
	);
};

const Quay = ({ quayIndex }: { quayIndex: number }) => {

	const { control } = useFormContext<LocationSchemaInput, unknown, LocationSchemaOutput>();

	const [ quays ] = useWatch({ control, name: [ "quays" ] });
	const bolders = quays?.[quayIndex]?.bolders || [];
	const existingBolders = [ ...bolders ].filter(b => b.id);

	const { selectedTab, selectTab } = useTabs<LocationQuayTabs>({
		defaultSelected: LocationQuayTabs.BOLDERS,
		param: `${quays[quayIndex].name1}-${quayIndex}`,
	});

	return (
		<Card disablePadding>
			<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
				<Tabs
					value={selectedTab}
					onChange={(_, value) => selectTab(value)}
					indicatorColor="primary"
					variant="scrollable">
					<Tab value={LocationQuayTabs.BOLDERS} label={Language("BOLDERS")} />

					{existingBolders.length > 0 ? (
						<Tab value={LocationQuayTabs.CRANES} label={Language("CRANES")} />
					) : (
						<Tooltip title={intl.formatMessage({ id: "ADD_SAVE_BOLDERS_FIRST" })} >
							<span>
								<Tab value={LocationQuayTabs.CRANES} label={Language("CRANES")} disabled />
							</span>
						</Tooltip>
					)}
				</Tabs>
			</Box>

			{selectedTab === LocationQuayTabs.BOLDERS && (
				<Box sx={{ padding: 1.5, width: "100%", overflowX: "auto" }}>
					<Bolders quayIndex={quayIndex} />
				</Box>
			)}

			{selectedTab === LocationQuayTabs.CRANES && (
				<Cranes quayIndex={quayIndex} />
			)}
		</Card>
	);
};

const Bolders = ({ quayIndex }: { quayIndex: number }) => {

	const { control } = useFormContext<LocationSchemaInput, unknown, LocationSchemaOutput>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: `quays.${quayIndex}.bolders`,
	});

	const defaultBolder = {
		id: 0,
		description: "",
		latitude: null,
		longitude: null,
		metersToNextBolder: null,
		phantomBolder: false,
		mooringSide: MooringSide.Port,
	};

	return (
		<div>
			<FormArrayOnlyRows
				array={fields}
				onAdd={() => append(defaultBolder)}
				onDelete={(index) => remove(index)}
				fields={[ {
					title: intl.formatMessage({ id: "BOLDER_DESCRIPTION" }),
					required: true,
					render: (index) => (
						<Controller
							name={`quays.${quayIndex}.bolders.${index}.description`}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					),
				}, {
					title: intl.formatMessage({ id: "BOLDER_LATITUDE" }),
					render: (index) => (
						<Controller
							name={`quays.${quayIndex}.bolders.${index}.latitude`}
							control={control}
							render={({ field }) => <NumberInput {...field} />} />
					),
				}, {
					title: intl.formatMessage({ id: "BOLDER_LONGITUDE" }),
					render: (index) => (
						<Controller
							name={`quays.${quayIndex}.bolders.${index}.longitude`}
							control={control}
							render={({ field }) => <NumberInput {...field} />} />
					),
				}, {
					title: intl.formatMessage({ id: "BOLDER_METERS_TO_NEXT_BOLDER" }),
					render: (index) => (
						<Controller
							name={`quays.${quayIndex}.bolders.${index}.metersToNextBolder`}
							control={control}
							render={({ field }) => <NumberInput {...field} decimalScale={0} />} />
					),
				}, {
					title: intl.formatMessage({ id: "BOLDER_PHANTOM_BOLDER" }),
					render: (index) => (
						<Controller
							name={`quays.${quayIndex}.bolders.${index}.phantomBolder`}
							control={control}
							render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
					),
				}, {
					title: intl.formatMessage({ id: "BOLDER_MOORING_SIDE" }),
					render: (index) => (
						<Controller
							name={`quays.${quayIndex}.bolders.${index}.mooringSide`}
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									fetchOptions={Object.values(MooringSide)}
									getOptionLabel={option => intl.formatMessage({ id: `BOLDER_MOORING_SIDE_${option}` })}
									clearable={false}
									disableSearch />
							)} />
					),
				} ]} />
		</div>
	);
};

const Cranes = ({ quayIndex }: { quayIndex: number }) => {

	const { control, setValue } = useFormContext<LocationSchemaInput, unknown, LocationSchemaOutput>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: `quays.${quayIndex}.cranes`,
	});

	const [ quays ] = useWatch({ control, name: [ "quays" ] });

	const bolders = quays?.[quayIndex]?.bolders || [];
	const cranes = quays?.[quayIndex]?.cranes || [];

	const defaultCrane = {
		id: 0,
		name: "",
		sequenceNumber: null,
		craneType: cranes[0] ? cranes[0].craneType : CraneType.Bulk,
		fromBolder: null,
		toBolder: null,

		averageCraneSpeedInMovesPerHour: null,
		maxHeightInMeters: null,
		maxWidthInMeters: null,
		outreachInContainers: null,
		liftCapacityInTons: null,

		twinningAllowed: false,
		fullContainersAllowed: false,
		canHandleOOG: false,
		canHandleBundles: false,

		handlingRates: [],
	};

	const onCraneTypeChange = (craneType: CraneType) => {
		cranes.forEach((crane, index) => {
			setValue(`quays.${quayIndex}.cranes.${index}.craneType`, craneType);
			setValue(`quays.${quayIndex}.cranes.${index}.averageCraneSpeedInMovesPerHour`, defaultCrane.averageCraneSpeedInMovesPerHour);
			setValue(`quays.${quayIndex}.cranes.${index}.maxHeightInMeters`, defaultCrane.maxHeightInMeters);
			setValue(`quays.${quayIndex}.cranes.${index}.maxWidthInMeters`, defaultCrane.maxWidthInMeters);
			setValue(`quays.${quayIndex}.cranes.${index}.outreachInContainers`, defaultCrane.outreachInContainers);
			setValue(`quays.${quayIndex}.cranes.${index}.liftCapacityInTons`, defaultCrane.liftCapacityInTons);
			setValue(`quays.${quayIndex}.cranes.${index}.twinningAllowed`, defaultCrane.twinningAllowed);
			setValue(`quays.${quayIndex}.cranes.${index}.fullContainersAllowed`, defaultCrane.fullContainersAllowed);
			setValue(`quays.${quayIndex}.cranes.${index}.canHandleOOG`, defaultCrane.canHandleBundles);
			setValue(`quays.${quayIndex}.cranes.${index}.canHandleBundles`, defaultCrane.canHandleBundles);
			setValue(`quays.${quayIndex}.cranes.${index}.handlingRates`, defaultCrane.handlingRates);
		});
	};

	return (
		<Box p={2} pt={fields.length === 0 ? 2 : 0}>
			{fields.map((item, index) => {

				const crane = quays?.[quayIndex]?.cranes?.[index];

				return (
					<Card
						key={item.id}
						title={Language("MOORING_AVAILABILITIES_CRANE")}
						subtitle={quays?.[quayIndex]?.cranes?.[index]?.name}
						actions={[ {
							onClick: () => remove(index),
							icon: <DeleteIcon />,
						} ]}
						collapsable>
						<Grid spacing={3} container>
							<Grid size={{ lg: 3, md: 3, xs: 12 }}>
								<FormGroup label={Language("CRANE_NAME")} overflow="hidden" required>
									<Controller
										name={`quays.${quayIndex}.cranes.${index}.name`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							<Grid size={{ lg: 3, md: 3, xs: 12 }}>
								<FormGroup label={Language("SEQUENCE_NUMBER")} overflow="hidden">
									<Controller
										name={`quays.${quayIndex}.cranes.${index}.sequenceNumber`}
										control={control}
										render={({ field }) => <NumberInput {...field} decimalScale={0} />} />
								</FormGroup>
							</Grid>

							<Grid size={{ lg: 3, md: 3, xs: 12 }}>
								<FormGroup label={Language("CRANE_TYPE")} overflow="hidden">
									<Controller
										name={`quays.${quayIndex}.cranes.${index}.craneType`}
										control={control}
										render={({ field }) => (
											<Select
												{...field}
												onChange={(option) => {
													field.onChange(option);

													if (option) {
														onCraneTypeChange(option);
													}
												}}
												fetchOptions={Object.values(CraneType)}
												getOptionLabel={option => intl.formatMessage({ id: `CRANE_TYPE_${option === CraneType.Container ? "1" : "2"}` })}
												clearable={false}
												disabled={index > 0}
												disableSearch />
										)} />

								</FormGroup>
							</Grid>

							<Grid size={{ lg: 3, md: 3, xs: 12 }}>
								<FormGroup label={Language("CRANE_BOLDER_FROM")} overflow="hidden" required>
									<Controller
										name={`quays.${quayIndex}.cranes.${index}.fromBolder`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Select
													{...field}
													fetchOptions={[ ...bolders ].filter(it => it.id).map(it => ({ ...it, id: it.id as number }))}
													getOptionLabel={b => b.description} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							<Grid size={{ lg: 3, md: 3, xs: 12 }}>
								<FormGroup label={Language("CRANE_BOLDER_TO")} overflow="hidden" required>
									<Controller
										name={`quays.${quayIndex}.cranes.${index}.toBolder`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Select
													{...field}
													fetchOptions={[ ...bolders ].filter(it => it.id).map(it => ({ ...it, id: it.id as number }))}
													getOptionLabel={b => b.description} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							{crane?.craneType === CraneType.Container ? (
								<React.Fragment>
									<Grid size={{ lg: 3, md: 3, xs: 12 }}>
										<FormGroup label={Language("CRANE_SPEED")} overflow="hidden">
											<Controller
												name={`quays.${quayIndex}.cranes.${index}.averageCraneSpeedInMovesPerHour`}
												control={control}
												render={({ field }) => (
													<NumberInput {...field} decimalScale={0} addonAfter="m/h" />
												)} />
										</FormGroup>
									</Grid>

									<Grid size={{ lg: 3, md: 3, xs: 12 }}>
										<FormGroup label={Language("CRANE_HEIGHT")} overflow="hidden">
											<Controller
												name={`quays.${quayIndex}.cranes.${index}.maxHeightInMeters`}
												control={control}
												render={({ field }) => (
													<NumberInput {...field} addonAfter="m" />
												)} />
										</FormGroup>
									</Grid>

									<Grid size={{ lg: 3, md: 3, xs: 12 }}>
										<FormGroup label={Language("CRANE_MAXWIDTH")} overflow="hidden">
											<Controller
												name={`quays.${quayIndex}.cranes.${index}.maxWidthInMeters`}
												control={control}
												render={({ field }) => (
													<NumberInput {...field} addonAfter="m" />
												)} />
										</FormGroup>
									</Grid>

									<Grid size={{ lg: 3, md: 3, xs: 12 }}>
										<FormGroup label={Language("CRANE_OUTREACH")} overflow="hidden">
											<Controller
												name={`quays.${quayIndex}.cranes.${index}.outreachInContainers`}
												control={control}
												render={({ field }) => <NumberInput {...field} decimalScale={0} />} />
										</FormGroup>
									</Grid>

									<Grid size={{ lg: 3, md: 3, xs: 12 }}>
										<FormGroup label={Language("CRANE_LIFT_CAPACITY")} overflow="hidden">
											<Controller
												name={`quays.${quayIndex}.cranes.${index}.liftCapacityInTons`}
												control={control}
												render={({ field }) => (
													<NumberInput {...field} addonAfter={intl.formatMessage({ id: "TON" })} />
												)} />
										</FormGroup>
									</Grid>

									<Grid size={{ lg: 3, md: 3, xs: 12 }}>
										<FormGroup label={Language("CRANE_TWINNING_ALLOWED")} overflow="hidden">
											<Controller
												name={`quays.${quayIndex}.cranes.${index}.twinningAllowed`}
												control={control}
												render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
										</FormGroup>
									</Grid>

									<Grid size={{ lg: 3, md: 3, xs: 12 }}>
										<FormGroup label={Language("CRANE_CAN_HANDLE_FULL_CONTAINERS")} overflow="hidden">
											<Controller
												name={`quays.${quayIndex}.cranes.${index}.fullContainersAllowed`}
												control={control}
												render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
										</FormGroup>
									</Grid>

									<Grid size={{ lg: 3, md: 3, xs: 12 }}>
										<FormGroup label={Language("CRANE_CAN_HANDLE_OOG")} overflow="hidden">
											<Controller
												name={`quays.${quayIndex}.cranes.${index}.canHandleOOG`}
												control={control}
												render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
										</FormGroup>
									</Grid>

									<Grid size={{ lg: 3, md: 3, xs: 12 }}>
										<FormGroup label={Language("CRANE_CAN_HANDLE_BUNDLES")} overflow="hidden">
											<Controller
												name={`quays.${quayIndex}.cranes.${index}.canHandleBundles`}
												control={control}
												render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
										</FormGroup>
									</Grid>
								</React.Fragment>
							) : (
								<Grid size={{ lg: 6, md: 6, xs: 12 }}>
									<ProductHandlingRates quayIndex={quayIndex} craneIndex={index} />
								</Grid>
							)}
						</Grid>
					</Card>
				);
			})}

			<Box>
				<Button
					icon={<AddIcon />}
					onClick={() => append(defaultCrane)}
					size="small"
					variant="outlined" />
			</Box>
		</Box>
	);
};

const ProductHandlingRates = ({ quayIndex, craneIndex }: { quayIndex: number, craneIndex: number }) => {

	const { control } = useFormContext<LocationSchemaInput, unknown, LocationSchemaOutput>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: `quays.${quayIndex}.cranes.${craneIndex}.handlingRates`,
	});

	const defaultHandlingRate = {
		id: 0,
		product: null,
		handlingRateInTonsPerHour: null,
	};

	return (
		<div>
			<FormArrayOnlyRows
				array={fields}
				onAdd={() => append(defaultHandlingRate)}
				onDelete={(index) => remove(index)}
				fields={[ {
					title: intl.formatMessage({ id: "PRODUCT" }),
					required: true,
					render: (index) => (
						<Controller
							name={`quays.${quayIndex}.cranes.${craneIndex}.handlingRates.${index}.product`}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<ProductSelect
										{...field}
										placeholder={intl.formatMessage({ id: "PRODUCT" })} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					),
				}, {
					title: intl.formatMessage({ id: "CRANE_SPEED" }),
					render: (index) => (
						<Controller
							name={`quays.${quayIndex}.cranes.${craneIndex}.handlingRates.${index}.handlingRateInTonsPerHour`}
							control={control}
							render={({ field }) => (
								<NumberInput {...field} addonAfter={`${intl.formatMessage({ id: "TON" })}/h`} />
							)} />
					),
				} ]} />
		</div>
	);
};

export default LocationQuaysForm;
